<template>
  <div class="auth-main v1">
    <div class="auth-wrapper">
      <div class="auth-form">
        <div class="card my-5">
          <div class="card-body">
            <div class="text-center">
              <img src="/src/assets/images/logo/vesproc120.png" alt="VESPROC" class="img-fluid mb-3" />
              <h4 class="f-w-500 mb-1">{{ $t('auth.forgotPassword.title') }}</h4>
              <p class="mb-3">{{ $t('auth.forgotPassword.subtitle') }}</p>
            </div>
            
            <!-- Alert messages -->
            <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
              {{ error }}
              <button type="button" class="btn-close" @click="error = null"></button>
            </div>
            <div v-if="successMessage" class="alert alert-success alert-dismissible fade show" role="alert">
              {{ successMessage }}
              <button type="button" class="btn-close" @click="successMessage = null"></button>
            </div>
            
            <div v-if="!successMessage">
              <div class="mb-3">
                <input
                  type="email"
                  class="form-control"
                  id="floatingInput"
                  :placeholder="$t('auth.forgotPassword.email')"
                  v-model="email"
                  required
                />
                <div v-if="v$.email.$error" class="text-danger text-start">
                  {{ v$.email.$errors[0].$message }}
                </div>
              </div>
              
              <div class="d-grid mt-4">
                <button type="button" class="btn btn-primary" @click="resetPassword" :disabled="isLoading">
                  <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
                  {{ $t('auth.forgotPassword.resetButton') }}
                </button>
              </div>
            </div>
            
            <div class="text-center mt-4">
              <p class="mb-0">
                {{ $t('auth.forgotPassword.rememberedPassword') }}
                <router-link to="/login" class="link-primary ms-1">{{ $t('auth.forgotPassword.login') }}</router-link>
              </p>
            </div>
          </div>
        </div>
      </div>
      <AuthFooter />
    </div>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import useVuelidate from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import authService from '@/services/authService';
import AuthFooter from '@/components/auth/AuthFooter.vue';

export default {
  components: {
    AuthFooter
  },
  name: 'ForgotPassword',
  setup() {
    const store = useStore();
    const router = useRouter();
    const { t, locale } = useI18n();
    
    const email = ref('');
    const error = ref(null);
    const successMessage = ref(null);
    const isLoading = ref(false);

    // Validation rules
    const rules = {
      email: { 
        required: required, 
        email: email 
      }
    };

    const v$ = useVuelidate(rules, { email });

    const resetPassword = async () => {
      error.value = null;
      
      // Validate form
      const isFormValid = await v$.value.$validate();
      if (!isFormValid) return;
      
      isLoading.value = true;
      
      try {
        await authService.requestPasswordReset(email.value);
        
        // Always show success message for security (don't reveal if email exists)
        successMessage.value = t('auth.forgotPassword.success');
        
        // Reset form
        email.value = '';
        v$.value.$reset();
      } catch (err) {
        // Handle rate limiting and other errors
        error.value = err.response?.data?.error?.message || 
                     err.response?.data?.message || 
                     t('auth.forgotPassword.error');
        console.error('Password reset error:', err);
      } finally {
        isLoading.value = false;
      }
    };

    return {
      email,
      error,
      successMessage,
      isLoading,
      resetPassword,
      v$
    };
  }
};
</script>

<style scoped>
.auth-main.v1 {
  background-color: #f2f2f2;
  background-image: url('/src/assets/images/logo/worldmap.png');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}

</style> 